import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { User, Bell, Save, Loader2, Check, ChevronRight, X } from 'lucide-react';
import useAuthStore from '../stores/authStore';
import api from '../api/client';

// "System" (System Configuration -- trial duration/grace period/system email)
// is hidden from the tab list on request. The tab content, save handler, and
// backend calls are left in place (unreachable while excluded here) so it can
// be restored later by adding the tab entry back.
const TABS = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
];

export default function Settings() {
    const { canSeeAsSuperAdmin, canActAsSuperAdmin, changePassword } = useAuthStore();
    // System tab: platform accounts see it; only superadmin / write access saves it.
    const canSeeSystem = canSeeAsSuperAdmin();
    const canEditSystem = canActAsSuperAdmin();
    const [activeTab, setActiveTab] = useState('profile');
    const [isSaving, setIsSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });

    const [profile, setProfile] = useState({ name: '', email: '', phone: '', role: '' });
    // Settings > Notifications: every platform-team account x every alert
    // type, from GET /internal/notification-recipients — { alerts, users, can_edit }.
    const [recipients, setRecipients] = useState(null);
    const [recipientsError, setRecipientsError] = useState(null);
    const [systemSettings, setSystemSettings] = useState({ trial_duration_days: 14, grace_period_days: 7, system_email: '' });

    useEffect(() => {
        api.get('/internal/auth/me').then((res) => {
            const d = res.data;
            setProfile({ name: d.name || '', email: d.email || '', phone: d.phone || '', role: d.role });
        }).catch(() => { /* profile load is best-effort */ });
    }, []);

    useEffect(() => {
        if (activeTab !== 'notifications') return;
        setRecipientsError(null);
        api.get('/internal/notification-recipients')
            .then((res) => setRecipients(res.data))
            .catch((err) => setRecipientsError(err.response?.data?.detail || 'Could not load who gets alerts'));
    }, [activeTab]);

    const toggleAlert = (userId, key) => setRecipients((r) => ({
        ...r,
        users: r.users.map((u) => (u.id === userId ? { ...u, alerts: { ...u.alerts, [key]: !u.alerts[key] } } : u)),
    }));

    useEffect(() => {
        if (activeTab === 'system' && canSeeSystem) {
            api.get('/internal/platform-settings').then((res) => setSystemSettings(res.data)).catch(() => { });
        }
    }, [activeTab, canSeeSystem]);

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        try {
            if (activeTab === 'profile') {
                await api.post('/auth/me', { name: profile.name, email: profile.email, phone: profile.phone });
            } else if (activeTab === 'notifications') {
                const res = await api.put('/internal/notification-recipients', {
                    users: recipients.users.map((u) => ({ id: u.id, alerts: u.alerts })),
                });
                setRecipients(res.data);
            } else if (activeTab === 'system' && canEditSystem) {
                await api.put('/internal/platform-settings', systemSettings);
            }
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to save settings');
        } finally {
            setIsSaving(false);
        }
    };

    const handleChangePassword = async () => {
        if (passwordForm.new !== passwordForm.confirm) {
            setError('New passwords do not match');
            return;
        }
        setIsSaving(true);
        setError(null);
        try {
            const result = await changePassword(passwordForm.current, passwordForm.new);
            if (result.success) {
                setShowPasswordModal(false);
                setPasswordForm({ current: '', new: '', confirm: '' });
                setSaved(true);
                setTimeout(() => setSaved(false), 2000);
            } else {
                setError(result.error || 'Failed to change password');
            }
        } catch (err) {
            setError(err.message || 'Failed to change password');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="settings">
            <div className="page-header">
                <h1 className="page-title">Settings</h1>
                <p className="page-subtitle">Manage your account and system preferences</p>
            </div>

            <div className="settings-layout">
                {/* Sidebar */}
                <nav className="settings-nav glass-card">
                    {TABS.map(tab => {
                        if (tab.id === 'system' && !canSeeSystem) return null;
                        const Icon = tab.icon;
                        return (
                            <button key={tab.id} className={`settings-nav__item ${activeTab === tab.id ? 'settings-nav__item--active' : ''}`} onClick={() => setActiveTab(tab.id)}>
                                <Icon size={18} />
                                <span>{tab.label}</span>
                                <ChevronRight size={16} className="settings-nav__chevron" />
                            </button>
                        );
                    })}
                </nav>

                {/* Content */}
                <div className="settings-content glass-card">
                    {error && (
                        <div className="alert alert--error" style={{ marginBottom: 'var(--space-5)', padding: 'var(--space-3)', background: 'var(--status-danger-subtle)', color: 'var(--status-danger)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)' }}>
                            {error}
                        </div>
                    )}

                    {activeTab === 'profile' && (
                        <div className="settings-section">
                            <h2 className="settings-section__title">Profile Information</h2>
                            <p className="settings-section__desc">Update your account details</p>
                            <div className="form-grid">
                                <div className="input-group">
                                    <label className="input-label">Name</label>
                                    <input type="text" className="input" value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} />
                                </div>
                                <div className="input-group">
                                    <label className="input-label">Email</label>
                                    <input type="email" className="input" value={profile.email} onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))} />
                                </div>
                                <div className="input-group">
                                    <label className="input-label">Phone</label>
                                    <input type="tel" className="input" value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} />
                                </div>
                                <div className="input-group">
                                    <label className="input-label">Role</label>
                                    <input type="text" className="input" value={profile.role === 'superadmin' ? 'Super Admin' : profile.role} disabled />
                                </div>
                            </div>

                            <div className="security-options" style={{ marginTop: 'var(--space-8)' }}>
                                <div className="security-item">
                                    <div className="security-item__info">
                                        <h4>Change Password</h4>
                                        <p>Update your password regularly for security</p>
                                    </div>
                                    <button className="btn btn--secondary btn--sm" onClick={() => setShowPasswordModal(true)}>Change</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'notifications' && (
                        <div className="settings-section">
                            <h2 className="settings-section__title">Notifications</h2>
                            <p className="settings-section__desc">
                                Tick who gets each alert. Emails go to each person&apos;s login email.
                                {recipients && !recipients.can_edit && ' You have read-only access, so you can see this but not change it.'}
                            </p>
                            {recipientsError && <p className="recipients-error">{recipientsError}</p>}
                            {!recipients && !recipientsError && (
                                <p className="recipients-hint"><Loader2 size={14} className="spin" /> Loading…</p>
                            )}
                            {recipients && (
                                <div className="recipients-scroll">
                                    <table className="recipients-table">
                                        <thead>
                                            <tr>
                                                <th className="recipients-person">Person</th>
                                                {recipients.alerts.map((a) => <th key={a.key}>{a.label}</th>)}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recipients.users.map((u) => (
                                                <tr key={u.id} className={u.active ? '' : 'is-inactive'}>
                                                    <td className="recipients-person">
                                                        <div className="recipients-name">{u.name || u.email}</div>
                                                        <div className="recipients-email">
                                                            {u.email}{!u.active && ' · Inactive, gets no emails'}
                                                        </div>
                                                    </td>
                                                    {recipients.alerts.map((a) => (
                                                        <td key={a.key} className="recipients-cell">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!u.alerts[a.key]}
                                                                disabled={!recipients.can_edit}
                                                                onChange={() => toggleAlert(u.id, a.key)}
                                                                aria-label={`${a.label} for ${u.name || u.email}`}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <p className="recipients-hint">
                                Someone missing? Add them on the <Link to="/users">Users</Link> page first.
                            </p>
                        </div>
                    )}

                    {activeTab === 'system' && canSeeSystem && (
                        <div className="settings-section">
                            <h2 className="settings-section__title">System Configuration</h2>
                            <p className="settings-section__desc">
                                Global system settings{canEditSystem ? '' : ' — read-only access, you can view but not change them'}
                            </p>
                            <div className="form-grid">
                                <div className="input-group">
                                    <label className="input-label">Trial Duration (days)</label>
                                    <input
                                        type="number"
                                        className="input"
                                        value={systemSettings.trial_duration_days}
                                        disabled={!canEditSystem}
                                        onChange={(e) => setSystemSettings((s) => ({ ...s, trial_duration_days: Number(e.target.value) }))}
                                    />
                                </div>
                                <div className="input-group">
                                    <label className="input-label">Grace Period (days)</label>
                                    <input
                                        type="number"
                                        className="input"
                                        value={systemSettings.grace_period_days}
                                        disabled={!canEditSystem}
                                        onChange={(e) => setSystemSettings((s) => ({ ...s, grace_period_days: Number(e.target.value) }))}
                                    />
                                </div>
                                <div className="input-group full-width">
                                    <label className="input-label">System Email</label>
                                    <input
                                        type="email"
                                        className="input"
                                        value={systemSettings.system_email || ''}
                                        disabled={!canEditSystem}
                                        onChange={(e) => setSystemSettings((s) => ({ ...s, system_email: e.target.value }))}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Save Button — hidden on Notifications for a read-only viewer, and until it has loaded */}
                    {!(activeTab === 'notifications' && !recipients?.can_edit) && !(activeTab === 'system' && !canEditSystem) && (
                        <div className="settings-actions">
                            <button className="btn btn--primary" onClick={handleSave} disabled={isSaving}>
                                {saved ? <><Check size={16} /> Saved</> : isSaving ? <><Loader2 size={16} className="spin" /> Saving...</> : <><Save size={16} /> Save Changes</>}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Password Change Modal */}
            {showPasswordModal && (
                <div className="modal-backdrop" onClick={() => setShowPasswordModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal__header">
                            <h3 className="modal__title">Change Password</h3>
                            <button className="modal__close" onClick={() => setShowPasswordModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal__body">
                            {error && (
                                <div className="alert alert--error" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--status-danger-subtle)', color: 'var(--status-danger)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)' }}>
                                    {error}
                                </div>
                            )}
                            <div className="input-group" style={{ marginBottom: 'var(--space-4)' }}>
                                <label className="input-label">Current Password</label>
                                <input
                                    type="password"
                                    className="input"
                                    placeholder="Enter current password"
                                    value={passwordForm.current}
                                    onChange={e => setPasswordForm({ ...passwordForm, current: e.target.value })}
                                />
                            </div>
                            <div className="input-group" style={{ marginBottom: 'var(--space-4)' }}>
                                <label className="input-label">New Password</label>
                                <input
                                    type="password"
                                    className="input"
                                    placeholder="Enter new password (min 8 characters)"
                                    value={passwordForm.new}
                                    onChange={e => setPasswordForm({ ...passwordForm, new: e.target.value })}
                                />
                            </div>
                            <div className="input-group">
                                <label className="input-label">Confirm New Password</label>
                                <input
                                    type="password"
                                    className="input"
                                    placeholder="Confirm new password"
                                    value={passwordForm.confirm}
                                    onChange={e => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="modal__footer">
                            <button className="btn btn--secondary" onClick={() => setShowPasswordModal(false)}>
                                Cancel
                            </button>
                            <button className="btn btn--primary" onClick={handleChangePassword} disabled={isSaving}>
                                {isSaving ? <><Loader2 size={16} className="spin" /> Changing...</> : 'Change Password'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        .settings {
          /* noLag rebrand: scoped to this page only. Confirmed via
             get_design_context on 2543:2 ("Setting") — title/subtitle
             match exactly. Figma's sub-nav shows a 4th "Security" tab and
             an active "System" tab neither of which exist here — the
             System tab is deliberately hidden per the comment at the top
             of this file ("on request"), and there's no separate Security
             tab (Change Password lives inside Profile instead); neither is
             being added back, that's an existing product decision, not an
             oversight. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);

          max-width: 1000px;
        }
        /* Figma-confirmed white-sheen recipe for both cards (2545:387 /
           2545:437) — siblings, not nested, so each keeping its own
           backdrop-filter is safe. */
        .settings .glass-card {
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%);
          backdrop-filter: blur(14px) saturate(180%);
          -webkit-backdrop-filter: blur(14px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45), 0 20px 48px rgba(0, 0, 0, 0.5);
        }
        .settings .glass-card::before { display: none; }
        /* The nav card is 18px in Figma, not 16px like the content card. */
        .settings .settings-nav.glass-card { border-radius: 18px; }
        .page-header { margin-bottom: var(--space-8); }
        .page-title { font-size: var(--text-2xl); font-weight: 600; margin-bottom: var(--space-2); letter-spacing: -0.02em; }
        .page-subtitle { font-size: var(--text-sm); color: var(--text-tertiary); }

        .settings-layout { display: grid; grid-template-columns: 240px 1fr; gap: var(--space-6); }

        .settings-nav { padding: var(--space-3); display: flex; flex-direction: column; gap: var(--space-1); }
        .settings-nav__item {
          display: flex; align-items: center; gap: var(--space-3);
          padding: var(--space-3) var(--space-4); border-radius: var(--radius-md);
          background: transparent; border: none; color: var(--text-secondary);
          font-size: var(--text-sm); cursor: pointer; text-align: left;
          transition: all var(--duration-fast) var(--ease-out);
        }
        .settings-nav__item:hover { background: rgba(255,255,255,0.04); color: var(--text-primary); }
        /* Figma (2545:388) shows the active tab as flat #262626 with white
           text — same pattern already confirmed for Sidebar.jsx's main nav
           (orange is reserved for the left indicator bar there, not used
           as a background/text tint) — not the orange-subtle treatment
           this previously used. */
        .settings-nav__item--active { background: #262626; color: var(--text-primary); font-weight: 600; }
        .settings-nav__chevron { margin-left: auto; opacity: 0; transition: opacity var(--duration-fast); }
        .settings-nav__item:hover .settings-nav__chevron, .settings-nav__item--active .settings-nav__chevron { opacity: 1; }

        .settings-content { padding: var(--space-8); }
        .settings-section__title { font-size: var(--text-lg); font-weight: 600; margin-bottom: var(--space-2); }
        .settings-section__desc { font-size: var(--text-sm); color: var(--text-tertiary); margin-bottom: var(--space-6); }

        .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: var(--space-5); }
        .full-width { grid-column: 1 / -1; }

        .security-options { display: flex; flex-direction: column; gap: var(--space-4); }
        .security-item {
          display: flex; align-items: center; justify-content: space-between;
          padding: var(--space-4); background: rgba(0,0,0,0.2); border-radius: var(--radius-md);
        }
        .security-item__info h4 { font-size: var(--text-sm); font-weight: 500; margin-bottom: var(--space-1); display: flex; align-items: center; }
        .security-item__info p { font-size: var(--text-xs); color: var(--text-tertiary); }

        /* Notifications: people x alert types. Scrolls sideways on a narrow
           screen rather than squeezing five columns of labels. */
        .recipients-scroll { overflow-x: auto; border-radius: var(--radius-md); background: rgba(0,0,0,0.2); }
        .recipients-table { width: 100%; border-collapse: collapse; font-size: var(--text-sm); }
        .recipients-table th {
          padding: var(--space-3); font-size: var(--text-xs); font-weight: 500; color: var(--text-tertiary);
          text-align: center; vertical-align: bottom; border-bottom: 1px solid var(--divider); min-width: 80px;
        }
        .recipients-table td { padding: var(--space-3); border-bottom: 1px solid var(--divider); }
        .recipients-table tr:last-child td { border-bottom: none; }
        .recipients-table .recipients-person { text-align: left; min-width: 170px; }
        .recipients-name { font-weight: 500; }
        .recipients-email { font-size: var(--text-xs); color: var(--text-tertiary); margin-top: 2px; }
        .recipients-table tr.is-inactive .recipients-name { color: var(--text-tertiary); }
        .recipients-cell { text-align: center; }
        .recipients-cell input { width: 18px; height: 18px; accent-color: var(--accent-primary); cursor: pointer; }
        .recipients-cell input:disabled { cursor: default; }
        .recipients-hint { margin-top: var(--space-4); font-size: var(--text-xs); color: var(--text-tertiary); display: flex; align-items: center; gap: var(--space-2); }
        .recipients-hint a { color: var(--accent-primary); }
        .recipients-error { margin-bottom: var(--space-4); font-size: var(--text-sm); color: var(--status-danger); }

        .settings-actions { margin-top: var(--space-8); padding-top: var(--space-6); border-top: 1px solid var(--divider); display: flex; justify-content: flex-end; }

        @media (max-width: 768px) { .settings-layout { grid-template-columns: 1fr; } .form-grid { grid-template-columns: 1fr; } }
      `}</style>
        </div>
    );
}
